/**
 * The pieces a chat is made of, shared by the Chat page and the task page's Discussion card:
 * MessageItem (one message with files, task card, reactions and actions), Composer, MessageList, ReplyPanel,
 * TaskDiscussion (the task's thread with the task's changes woven in) and ShareTaskDialog.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import clsx from "clsx";
import { CheckSquare, CornerDownRight, FileText, ListChecks, MessageCircle, Paperclip, Pencil, Pin, PinOff, Send, SmilePlus, Trash2, X } from "lucide-react";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { api, del, patch, post } from "../../lib/api";
import { MENTION_RE, setActiveConversation, useConversations, useTyping, type Message, type TaskCard as TaskCardT } from "../../lib/chat";
import { newClientRef, sendOrQueue } from "../../lib/offline";
import { useSession } from "../../lib/session";
import { Avatar, Empty, ErrorNote, Modal } from "../ui";
import { Check, Eye } from "lucide-react";
import { PersonPicker, UploadButton } from "../work";

export const renderBody = (b: string) => b.split(MENTION_RE).map((part, i) => (i % 3 === 1 ? <strong key={i} className="text-brand-700">@{part}</strong> : i % 3 === 2 ? null : <span key={i}>{part}</span>));
export const fmtTime = (iso: string) => new Date(iso).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
export const fmtDay = (iso: string) => {
  const d = new Date(iso); const today = new Date();
  const same = (a: Date, b: Date) => a.toDateString() === b.toDateString();
  if (same(d, today)) return "Today";
  const y = new Date(today); y.setDate(y.getDate() - 1);
  if (same(d, y)) return "Yesterday";
  return d.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" });
};
export const fmtWhen = (iso: string) => { const d = new Date(iso); return d.toDateString() === new Date().toDateString() ? fmtTime(iso) : d.toLocaleDateString("en-GB", { day: "numeric", month: "short" }); };
const fmtSize = (n: number) => (n > 1_000_000 ? `${(n / 1_000_000).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1000))} KB`);

export const invalidateChat = (qc: ReturnType<typeof useQueryClient>, conversationId: string) => {
  void qc.invalidateQueries({ queryKey: ["chat", "messages", conversationId] });
  void qc.invalidateQueries({ queryKey: ["chat", "conversations"] });
  void qc.invalidateQueries({ queryKey: ["chat", "unread"] });
};

/** A task shared into a chat, with its live status and how busy its own thread is. */
export function TaskCard({ t }: { t: TaskCardT }) {
  const closed = t.state === "done" || t.state === "cancelled";
  return (
    <Link to="/work/$id" params={{ id: t.id }} className="mt-1.5 flex max-w-md items-center gap-3 rounded-xl border border-[var(--line)] bg-[var(--panel)] px-3 py-2 hover:border-brand-300">
      <ListChecks size={18} className="shrink-0 text-brand-600" />
      <span className="min-w-0 flex-1">
        <span className={clsx("block truncate text-sm font-semibold", closed && "text-[var(--muted)] line-through")}>{t.title}</span>
        <span className="muted block truncate text-xs">{t.ref}{t.assigneeNames?.length ? ` · ${t.assigneeNames.join(", ")}` : t.ownerName ? ` · ${t.ownerName}` : ""}{t.dueAt ? ` · due ${new Date(t.dueAt).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}` : ""}</span>
        {t.threadCount > 0 && <span className="mt-0.5 block text-xs font-medium text-brand-700"><CornerDownRight size={11} className="inline" /> {t.threadCount} {t.threadCount === 1 ? "message" : "messages"} in the task thread{t.threadLastAt ? ` · last ${fmtWhen(t.threadLastAt)}` : ""}</span>}
      </span>
      <span className={clsx("chip shrink-0", t.state === "done" ? "text-emerald-700" : t.state === "in_progress" ? "text-brand-700" : "")}>{t.statusLabel ?? t.state.replace("_", " ")}</span>
    </Link>
  );
}

function Files({ files }: { files: Message["files"] }) {
  if (!files.length) return null;
  const images = files.filter((f) => f.type.startsWith("image/"));
  const others = files.filter((f) => !f.type.startsWith("image/"));
  return (
    <div className="mt-1.5 space-y-1.5">
      {images.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {images.map((f) => <a key={f.id} href={`/api/documents/${f.id}/content`} target="_blank" rel="noreferrer"><img src={`/api/documents/${f.id}/content`} alt={f.name} className="line h-28 max-w-[14rem] rounded-lg border object-cover" loading="lazy" /></a>)}
        </div>
      )}
      {others.map((f) => (
        <a key={f.id} href={`/api/documents/${f.id}/content`} target="_blank" rel="noreferrer" className="flex w-fit max-w-full items-center gap-2 rounded-lg border border-[var(--line)] bg-[var(--panel)] px-2.5 py-1.5 text-xs hover:border-brand-300">
          <FileText size={15} className="muted shrink-0" /><span className="truncate font-medium">{f.name}</span><span className="muted shrink-0">{fmtSize(f.size)}</span>
        </a>
      ))}
    </div>
  );
}

export function MessageItem({ m, grouped, emoji, onReply, inThread, compact, showSeen }: { m: Message; grouped?: boolean; emoji: string[]; onReply?: (m: Message) => void; inThread?: boolean; compact?: boolean; /** "Seen by …" under it (the sender's last message) */ showSeen?: boolean }) {
  const qc = useQueryClient();
  const me = useSession().person?.id;
  const nav = useNavigate();
  const [editing, setEditing] = useState<string | null>(null);
  const [picking, setPicking] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const done = () => invalidateChat(qc, m.conversationId);
  const edit = useMutation({ mutationFn: (body: string) => patch(`/api/chat/messages/${m.id}`, { body }), onSuccess: () => { setEditing(null); done(); }, onError: setError });
  const remove = useMutation({ mutationFn: () => del(`/api/chat/messages/${m.id}`), onSuccess: () => { done(); void qc.invalidateQueries({ queryKey: ["item"] }); }, onError: setError });
  const react = useMutation({ mutationFn: (e: string) => post(`/api/chat/messages/${m.id}/react`, { emoji: e }), onSuccess: () => { setPicking(false); done(); }, onError: setError });
  const pin = useMutation({ mutationFn: (pinned: boolean) => post(`/api/chat/messages/${m.id}/pin`, { pinned }), onSuccess: () => { done(); void qc.invalidateQueries({ queryKey: ["chat", "conversation", m.conversationId] }); }, onError: setError });
  const toTask = useMutation({ mutationFn: () => post<{ itemId: string; ref: string }>(`/api/chat/messages/${m.id}/to-task`, {}), onSuccess: (r) => { done(); void nav({ to: "/work/$id", params: { id: r.itemId } }); }, onError: setError });
  const mine = m.authorId === me;
  const card = !!m.item && !m.body;
  return (
    <div className={clsx("group relative flex gap-3 rounded-lg px-2 hover:bg-[#f7f8fc]", grouped ? "py-0.5" : compact ? "mt-1 py-1" : "mt-2 py-1")}>
      <div className={clsx("shrink-0", compact ? "w-7" : "w-8")}>{!grouped && <Avatar name={m.authorName} size={compact ? 28 : 32} />}</div>
      <div className="min-w-0 flex-1">
        {!grouped && <div className="flex items-baseline gap-2"><span className="text-sm font-semibold">{m.authorName}</span><span className="muted text-[11px]">{fmtTime(m.createdAt)}</span>{m.pinned && <span className="muted inline-flex items-center gap-0.5 text-[11px]"><Pin size={10} /> pinned</span>}</div>}
        {editing !== null ? (
          <div className="mt-1">
            <textarea className="input min-h-[3rem]" value={editing} autoFocus onChange={(e) => setEditing(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); if (editing.trim()) edit.mutate(editing.trim()); } if (e.key === "Escape") setEditing(null); }} />
            <div className="mt-1 flex gap-2 text-xs"><button className="btn btn-sm" onClick={() => edit.mutate(editing.trim())} disabled={!editing.trim()}>Save</button><button className="btn btn-sm" onClick={() => setEditing(null)}>Cancel</button></div>
          </div>
        ) : (
          <>
            {m.deleted ? <p className="muted text-sm italic">This message was deleted</p> : m.body ? (
              <p className={clsx("whitespace-pre-wrap break-words leading-relaxed", compact ? "text-sm" : "text-[0.9375rem]")} title={grouped ? fmtTime(m.createdAt) : undefined}>
                {renderBody(m.body)}{m.editedAt && <span className="muted ml-1 text-[11px]">(edited)</span>}
              </p>
            ) : card ? <p className="muted text-xs">shared a task</p> : null}
            {!m.deleted && <Files files={m.files} />}
            {!m.deleted && m.item && <TaskCard t={m.item} />}
          </>
        )}
        {(m.reactions.length > 0 || picking) && !m.deleted && (
          <div className="mt-1 flex flex-wrap items-center gap-1">
            {m.reactions.map((r) => (
              <button key={r.emoji} className={clsx("inline-flex items-center gap-1 rounded-full border px-1.5 py-0.5 text-xs", r.mine ? "border-brand-300 bg-brand-50 text-brand-700" : "border-[var(--line)] bg-[var(--panel)] hover:border-brand-300")} title={r.names.join(", ")} onClick={() => react.mutate(r.emoji)}>
                <span>{r.emoji}</span><span className="num font-semibold">{r.count}</span>
              </button>
            ))}
            {picking && emoji.map((e) => <button key={e} className="rounded-full px-1.5 py-0.5 text-base hover:bg-[#eceef5]" onClick={() => react.mutate(e)} aria-label={`React ${e}`}>{e}</button>)}
          </div>
        )}
        {m.pending && <p className="muted mt-0.5 inline-flex items-center gap-1 text-[11px]"><Check size={11} /> Saved on this phone, sends when you're back online</p>}
        {showSeen && m.seenBy.length > 0 && <p className="muted mt-0.5 inline-flex items-center gap-1 text-[11px]" title={m.seenBy.join(", ")}><Eye size={11} /> Seen by {m.seenBy.length <= 3 ? m.seenBy.map((n) => n.split(" ")[0]).join(", ") : `${m.seenBy.length} people`}</p>}
        {!inThread && m.replyCount > 0 && onReply && (
          <button className="mt-1 inline-flex items-center gap-1 text-xs font-medium text-brand-700 hover:underline" onClick={() => onReply(m)}><CornerDownRight size={12} /> {m.replyCount} {m.replyCount === 1 ? "reply" : "replies"}{m.lastReplyAt ? ` · last ${fmtWhen(m.lastReplyAt)}` : ""}</button>
        )}
        <ErrorNote error={error} />
      </div>
      {!m.deleted && !m.pending && editing === null && (
        <span className="absolute -top-2.5 right-2 hidden items-center gap-0.5 rounded-lg border border-[var(--line)] bg-[var(--panel)] p-0.5 shadow-sm group-hover:flex group-focus-within:flex">
          <button className="rounded p-1 hover:bg-[#eceef5]" aria-label="React" title="React" onClick={() => setPicking((x) => !x)}><SmilePlus size={14} /></button>
          {!inThread && onReply && !m.parentId && <button className="rounded p-1 hover:bg-[#eceef5]" aria-label="Reply in thread" title="Reply in thread" onClick={() => onReply(m)}><CornerDownRight size={14} /></button>}
          {m.body && !m.item && <button className="rounded p-1 hover:bg-[#eceef5]" aria-label="Make a task" title="Make a task from this" disabled={toTask.isPending} onClick={() => { if (confirm("Make a task from this message? It goes in your tasks and a card is posted here.")) toTask.mutate(); }}><CheckSquare size={14} /></button>}
          <button className="rounded p-1 hover:bg-[#eceef5]" aria-label={m.pinned ? "Unpin" : "Pin"} title={m.pinned ? "Unpin" : "Pin"} onClick={() => pin.mutate(!m.pinned)}>{m.pinned ? <PinOff size={14} /> : <Pin size={14} />}</button>
          {mine && m.body && <button className="rounded p-1 hover:bg-[#eceef5]" aria-label="Edit" title="Edit" onClick={() => setEditing(m.body)}><Pencil size={14} /></button>}
          {mine && <button className="rounded p-1 hover:bg-[#eceef5]" aria-label="Delete" title="Delete" onClick={() => { if (confirm("Delete this message?")) remove.mutate(); }}><Trash2 size={14} /></button>}
        </span>
      )}
    </div>
  );
}

export function Composer({ conversationId, parentId, placeholder, autoFocus, compact, onSent }: { conversationId: string; parentId?: string; placeholder: string; autoFocus?: boolean; compact?: boolean; onSent?: () => void }) {
  const qc = useQueryClient();
  const [draft, setDraft] = useState("");
  const [files, setFiles] = useState<Array<{ id: string; filename: string }>>([]);
  const [mentioning, setMentioning] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const box = useRef<HTMLTextAreaElement>(null);
  const me = useSession().person;
  const lastTyping = useRef(0);
  const typed = () => {
    // R3: tell the others at most every 3 s while typing
    const now = Date.now();
    if (now - lastTyping.current < 3000 || !navigator.onLine) return;
    lastTyping.current = now;
    void post(`/api/chat/conversations/${conversationId}/typing`, {}).catch(() => undefined);
  };
  const send = useMutation({
    networkMode: "always",
    mutationFn: async (b: { body: string; attachments: string[] }) => {
      const clientRef = newClientRef();
      const r = await sendOrQueue<Message>({ method: "POST", path: `/api/chat/conversations/${conversationId}/messages`, body: { ...b, parentId, clientRef }, label: `Send “${(b.body || "file").slice(0, 50)}”` });
      if (r.queued) {
        // show it at once, marked as waiting; the replay (offline.ts) refreshes the chat when it goes through
        const fake: Message = { id: clientRef, conversationId, parentId: parentId ?? null, authorId: me?.id ?? "", authorName: me?.name ?? "You", body: b.body, mentions: [], createdAt: new Date().toISOString(), editedAt: null, deleted: false, pinned: false, files: [], reactions: [], replyCount: 0, lastReplyAt: null, item: null, seenBy: [], pending: true };
        const key = parentId ? ["chat", "messages", conversationId, "thread", parentId] : ["chat", "messages", conversationId];
        qc.setQueryData<{ items: Message[]; more: boolean }>(key, (d) => (d ? { ...d, items: [...d.items, fake] } : { items: [fake], more: false }));
        return null;
      }
      return r.result;
    },
    onMutate: () => setError(null),
    onSuccess: (r) => { if (r) { invalidateChat(qc, conversationId); void qc.invalidateQueries({ queryKey: ["item"] }); } onSent?.(); },
    onError: (e, b) => { setError(e); setDraft((d) => d || b.body); setFiles((f) => (f.length ? f : b.attachments.map((id) => ({ id, filename: "file" })))); },
  });
  const submit = () => {
    const t = draft.trim();
    if ((!t && !files.length) || send.isPending) return;
    setDraft(""); setFiles([]);
    if (box.current) box.current.style.height = "auto";
    send.mutate({ body: t, attachments: files.map((f) => f.id) });
  };
  return (
    <div>
      <ErrorNote error={error} />
      {mentioning && (
        <div className="mb-2"><PersonPicker value={[]} multiple={false} placeholder="Who do you want to mention?" onChange={(p) => { const person = p[0]; if (person) setDraft((d) => `${d.replace(/@$/, "")}@[${person.name}](${person.id}) `); setMentioning(false); box.current?.focus(); }} /></div>
      )}
      {files.length > 0 && (
        <div className="mb-1.5 flex flex-wrap gap-1.5">
          {files.map((f) => <span key={f.id} className="chip inline-flex items-center gap-1"><Paperclip size={11} />{f.filename}<button aria-label={`Remove ${f.filename}`} onClick={() => setFiles((x) => x.filter((y) => y.id !== f.id))}><X size={11} /></button></span>)}
        </div>
      )}
      <div className="flex items-end gap-1.5 rounded-xl border border-[var(--line)] bg-white px-2 py-1.5 focus-within:border-brand-400">
        <textarea ref={box} className={clsx("flex-1 resize-none bg-transparent px-1 py-1.5 outline-none", compact ? "min-h-[2.25rem] text-sm" : "min-h-[2.5rem] text-[0.9375rem]", "max-h-40")} rows={1} placeholder={placeholder} value={draft} autoFocus={autoFocus}
          onChange={(e) => { setDraft(e.target.value); e.target.style.height = "auto"; e.target.style.height = `${Math.min(e.target.scrollHeight, 160)}px`; if (e.target.value.endsWith("@")) setMentioning(true); if (e.target.value) typed(); }}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); submit(); } }} aria-label="Message" />
        <button type="button" className="btn !min-h-0 !px-2 !py-1.5" title="Mention someone" aria-label="Mention someone" onClick={() => setMentioning((x) => !x)}>@</button>
        <UploadButton label={<Paperclip size={15} />} className="btn !min-h-0 !px-2 !py-1.5" accept="*/*" capture={false} onUploaded={(ids) => setFiles((f) => [...f, ...ids])} />
        <button type="button" className="btn btn-primary !min-h-0 !px-3 !py-1.5" disabled={(!draft.trim() && !files.length) || send.isPending} onClick={submit} aria-label="Send"><Send size={15} /></button>
      </div>
    </div>
  );
}

/** The top-level messages of a conversation with day separators and "show earlier". */
export function MessageList({ conversationId, emoji, onReply, compact, extra, filter }: { conversationId: string; emoji: string[]; onReply?: (m: Message) => void; compact?: boolean; extra?: Array<{ at: string; node: React.ReactNode; key: string }>; filter?: "all" | "messages" | "changes" }) {
  const qc = useQueryClient();
  const msgs = useQuery({ queryKey: ["chat", "messages", conversationId], queryFn: () => api<{ items: Message[]; more: boolean }>(`/api/chat/conversations/${conversationId}/messages`) });
  const [older, setOlder] = useState<Message[]>([]);
  const bottom = useRef<HTMLDivElement>(null);
  const all = useMemo(() => [...older, ...(msgs.data?.items ?? [])], [older, msgs.data]);
  const lastId = all[all.length - 1]?.id;
  useLayoutEffect(() => { if (!compact) bottom.current?.scrollIntoView({ block: "end" }); }, [lastId, conversationId, compact]);
  const loadOlder = async () => {
    const first = all[0];
    if (!first) return;
    const r = await api<{ items: Message[]; more: boolean }>(`/api/chat/conversations/${conversationId}/messages?before=${first.id}`);
    setOlder((o) => [...r.items, ...o]);
    if (!r.more) qc.setQueryData<{ items: Message[]; more: boolean }>(["chat", "messages", conversationId], (d) => (d ? { ...d, more: false } : d));
  };
  const me = useSession().person?.id;
  const typingNames = useTyping(conversationId);
  const myLast = [...all].reverse().find((m) => m.authorId === me && !m.deleted && !m.pending)?.id;
  type Row = { at: string; key: string; kind: "m"; m: Message } | { at: string; key: string; kind: "x"; node: React.ReactNode };
  const rows: Row[] = [
    ...(filter === "changes" ? [] : all.map((m): Row => ({ at: m.createdAt, key: m.id, kind: "m", m }))),
    ...(filter === "messages" ? [] : (extra ?? []).map((e): Row => ({ at: e.at, key: e.key, kind: "x", node: e.node }))),
  ].sort((a, b) => a.at.localeCompare(b.at));
  return (
    <>
      <ErrorNote error={msgs.error} />
      {msgs.data?.more && filter !== "changes" && <div className="pb-2 text-center"><button className="btn btn-sm" onClick={() => void loadOlder()}>Show earlier messages</button></div>}
      {msgs.isLoading && <p className="muted text-sm">Loading…</p>}
      {msgs.data && rows.length === 0 && <Empty>{filter === "changes" ? "No changes yet." : "No messages yet. Say hello."}</Empty>}
      {rows.map((r, n) => {
        const prev = rows[n - 1];
        const newDay = !prev || fmtDay(prev.at) !== fmtDay(r.at);
        const grouped = r.kind === "m" && !newDay && prev?.kind === "m" && prev.m.authorId === r.m.authorId && new Date(r.at).getTime() - new Date(prev.at).getTime() < 5 * 60_000;
        return (
          <div key={r.key}>
            {newDay && <div className="my-3 flex items-center gap-3 text-[11px] font-semibold uppercase tracking-wide text-[var(--muted)]"><span className="h-px flex-1 bg-[var(--line)]" />{fmtDay(r.at)}<span className="h-px flex-1 bg-[var(--line)]" /></div>}
            {r.kind === "m" ? <MessageItem m={r.m} grouped={grouped} emoji={emoji} onReply={onReply} compact={compact} showSeen={r.m.id === myLast} /> : r.node}
          </div>
        );
      })}
      {typingNames.length > 0 && <p className="muted mt-1 pl-11 text-xs">{typingNames.join(", ")} {typingNames.length === 1 ? "is" : "are"} typing…</p>}
      <div ref={bottom} />
    </>
  );
}

/** One thread: the first message, its replies, and a composer that replies. */
export function ReplyPanel({ conversationId, parentId, emoji, onClose }: { conversationId: string; parentId: string; emoji: string[]; onClose: () => void }) {
  const q = useQuery({ queryKey: ["chat", "messages", conversationId, "thread", parentId], queryFn: () => api<{ items: Message[] }>(`/api/chat/conversations/${conversationId}/messages?parent=${parentId}`) });
  const qc = useQueryClient();
  useEffect(() => {
    // the stream invalidates ["chat","messages",conversationId]; that prefix covers this thread too
  }, [qc]);
  const items = q.data?.items ?? [];
  const me = useSession().person?.id;
  const typingNames = useTyping(conversationId);
  const bottom = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => { bottom.current?.scrollIntoView({ block: "end" }); }, [items.length]);
  return (
    <div className="flex h-full flex-col">
      <header className="flex items-center gap-2 border-b border-[var(--line)] px-3 py-2.5"><CornerDownRight size={16} className="muted" /><h3 className="flex-1 text-sm font-semibold">Thread</h3><button className="btn btn-sm" aria-label="Close thread" onClick={onClose}><X size={14} /></button></header>
      <div className="flex-1 overflow-y-auto px-2 py-2">
        <ErrorNote error={q.error} />
        {items.map((m, n) => (
          <div key={m.id}>
            <MessageItem m={m} emoji={emoji} inThread compact showSeen={n === items.length - 1 && m.authorId === me} />
            {n === 0 && items.length > 1 && <div className="my-2 flex items-center gap-2 text-[11px] text-[var(--muted)]"><span className="h-px flex-1 bg-[var(--line)]" />{items.length - 1} {items.length === 2 ? "reply" : "replies"}<span className="h-px flex-1 bg-[var(--line)]" /></div>}
          </div>
        ))}
        {typingNames.length > 0 && <p className="muted mt-1 pl-9 text-xs">{typingNames.join(", ")} {typingNames.length === 1 ? "is" : "are"} typing…</p>}
        <div ref={bottom} />
      </div>
      <div className="border-t border-[var(--line)] px-2 py-2"><Composer conversationId={conversationId} parentId={parentId} placeholder="Reply…" autoFocus compact /></div>
    </div>
  );
}

/** Share a task into one of my chats as a card. */
export function ShareTaskDialog({ itemId, onClose }: { itemId: string; onClose: () => void }) {
  const convs = useConversations();
  const nav = useNavigate();
  const qc = useQueryClient();
  const [note, setNote] = useState("");
  const [to, setTo] = useState("");
  const m = useMutation({
    mutationFn: () => post<Message>(`/api/chat/conversations/${to}/messages`, { body: note.trim(), itemId, clientRef: newClientRef() }),
    onSuccess: () => { invalidateChat(qc, to); onClose(); void nav({ to: "/chat/$id", params: { id: to } }); },
  });
  const me = convs.data?.me ?? "";
  const list = (convs.data?.items ?? []).filter((c) => c.kind !== "task");
  return (
    <Modal title="Discuss in a chat" onClose={onClose} footer={<><button className="btn" onClick={onClose}>Cancel</button><button className="btn btn-primary" disabled={!to || m.isPending} onClick={() => m.mutate()}><MessageCircle size={14} /> Share</button></>}>
      <ErrorNote error={m.error} />
      <p className="muted mb-2 text-xs">A card for this task goes into the chat you pick. Replies there stay in this task's thread.</p>
      <select className="input" value={to} onChange={(e) => setTo(e.target.value)} aria-label="Chat">
        <option value="">Pick a channel or chat…</option>
        {list.map((c) => <option key={c.id} value={c.id}>{c.kind === "channel" ? "# " : ""}{c.kind === "channel" ? c.name : c.name ?? (c.members ?? []).filter((x) => x.id !== me).map((x) => x.name).join(", ")}</option>)}
      </select>
      <textarea className="input mt-2 min-h-[3rem]" placeholder="Say why (optional)" value={note} onChange={(e) => setNote(e.target.value)} />
    </Modal>
  );
}

/** The task page's Discussion: the task's thread, with its changes (logs, status moves, steps) woven in. */
export function TaskDiscussion({ itemId, title, changes }: { itemId: string; title: string; changes: Array<{ at: string; key: string; node: React.ReactNode }> }) {
  const qc = useQueryClient();
  const thread = useQuery({ queryKey: ["chat", "task", itemId], queryFn: () => api<{ conversationId: string }>(`/api/chat/task/${itemId}`) });
  const convs = useConversations();
  const [filter, setFilter] = useState<"all" | "messages" | "changes">("all");
  const [sharing, setSharing] = useState(false);
  const [reply, setReply] = useState<Message | null>(null);
  const cid = thread.data?.conversationId;
  // read marker: opening the task counts as reading its thread (and looking at it, so no notice for it)
  useEffect(() => { if (cid) setActiveConversation(cid); return () => setActiveConversation(null); }, [cid]);
  useEffect(() => { if (cid) void post(`/api/chat/conversations/${cid}/read`, {}).then(() => { void qc.invalidateQueries({ queryKey: ["chat", "unread"] }); void qc.invalidateQueries({ queryKey: ["inbox"] }); }); }, [cid, qc]);
  return (
    <div>
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <div className="tabs" role="tablist" aria-label="Show">
          {(["all", "messages", "changes"] as const).map((f) => <button key={f} role="tab" aria-selected={filter === f} className="tab capitalize" onClick={() => setFilter(f)}>{f}</button>)}
        </div>
        <span className="ml-auto flex items-center gap-2 text-xs">
          {cid && <Link to="/chat/$id" params={{ id: cid }} className="btn btn-sm"><MessageCircle size={13} /> Open in Chat</Link>}
          <button className="btn btn-sm" onClick={() => setSharing(true)}><CornerDownRight size={13} /> Discuss in a chat</button>
        </span>
      </div>
      <ErrorNote error={thread.error} />
      {cid ? (
        <>
          <MessageList conversationId={cid} emoji={convs.data?.emoji ?? []} compact extra={changes} filter={filter} onReply={setReply} />
          <div className="line mt-3 border-t pt-3"><Composer conversationId={cid} placeholder={`Write in this task's thread… use @ to mention someone`} compact /></div>
          {reply && (
            <div className="fixed inset-0 z-40 flex justify-end bg-black/30" onClick={() => setReply(null)}>
              <div className="h-full w-full max-w-md bg-[var(--panel)] shadow-xl" onClick={(e) => e.stopPropagation()}><ReplyPanel conversationId={cid} parentId={reply.id} emoji={convs.data?.emoji ?? []} onClose={() => setReply(null)} /></div>
            </div>
          )}
        </>
      ) : <p className="muted text-sm">Loading…</p>}
      {sharing && <ShareTaskDialog itemId={itemId} onClose={() => setSharing(false)} />}
      <span className="sr-only">{title}</span>
    </div>
  );
}
